import { AviationDocument } from '@/features/end-of-lease/types';
import { EOLMeta } from '@/models/docs';

/**
 * Within OCR, splitting takes SPLITTING_WEIGHT (70%) and classifying takes the rest.
 */
export const SPLITTING_WEIGHT = 0.7;
export const CLASSIFYING_WEIGHT = 1 - SPLITTING_WEIGHT;

type ProgressPhase = 'splitting' | 'classifying';

export interface RawOcrProgress {
    phase: ProgressPhase | null;
    current: number;
    total: number;
}

export interface OcrProgress extends RawOcrProgress {
    phaseLabel: string;
    progress: number;
}

/**
 * Computes weighted progress and phase label from raw OCR progress data.
 * Splitting phase: 0-70%, Classifying phase: 70-100%.
 */
export function computeOcrProgress(
    raw: RawOcrProgress | null | undefined,
): OcrProgress | null {
    if (!raw || raw.total === 0) {
        return null;
    }

    const phasePercent = (raw.current / raw.total) * 100;

    let progress: number;
    if (raw.phase === 'splitting') {
        progress = Math.round(phasePercent * SPLITTING_WEIGHT);
    } else if (raw.phase === 'classifying') {
        const splittingComplete = SPLITTING_WEIGHT * 100;
        progress = Math.round(
            splittingComplete + phasePercent * CLASSIFYING_WEIGHT,
        );
    } else {
        progress = Math.round(phasePercent);
    }

    const phaseLabel =
        raw.phase === 'splitting'
            ? 'Splitting'
            : raw.phase === 'classifying'
              ? 'Classifying'
              : 'Processing';

    return {
        ...raw,
        phaseLabel,
        progress,
    };
}

export type LLMModel = {
    id: string;
    displayName: string;
};

export type CommitInfo = {
    commitSha: string;
    commitMessage: string;
    commitDate: string;
    releaseVersion?: string;
    releaseDate?: string;
};

// Health checks
type LLMStatus = {
    name: string;
    keyPresent: string;
    envVarName: string;
};

export type DatabaseStatus = {
    version: string;
    databaseName: string;
    databaseSize: string;
    connectionCount: number;
    extensions: string[];
};

type OllamaStatus = {
    host: string;
    connected: boolean;
    models: string[];
};

export type MigrationStatus = {
    totalCount: number;
    latestName: string;
    latestDate: string;
};

type ETLStatus = {
    status: 'healthy' | 'error';
    totalAssets: number | null;
};

export type SystemStatus = {
    llmProviders: LLMStatus[];
    database: DatabaseStatus | null;
    ollama: OllamaStatus | null;
    migration: MigrationStatus | null;
    etl?: ETLStatus | null;
};

type JobTypeStats = {
    count: number;
    uniqueCustomers: number;
    uniqueTeams: number;
    avgProcessingTime: number;
    statusBreakdown: {
        picked: number;
        pending: number;
        failed: number;
        completed: number;
    };
};

type JobQueueSummary = {
    totalJobs: number;
    uniqueJobTypes: number;
    statusCounts: {
        picked: number;
    };
    jobTypesOverview: {
        [key: string]: number;
    };
};

export type JobQueueStats = {
    summary: JobQueueSummary;
    jobTypeStats: {
        [key: string]: JobTypeStats;
    };
};

export type MultipartUploadPart = {
    ETag: string;
    PartNumber: number;
};

export enum ProductType {
    endOfLease = 'end_of_lease',
    sbAgent = 'sb_agent',
    maintenanceReview = 'maintenance_review',
    iataBinder = 'iata_binder',
    asset360 = 'asset_360',
    transition360 = 'transition_360',
}

type Signature = {
    name?: string;
    signed?: boolean;
};

type DocMeta = {
    pages?: number[];
    name?: string;
    component?: {
        name?: string;
        partNumber?: string;
        serialNumber?: string;
    };
    effectiveDate?: string;
    signature?: Signature;
    raw?: AviationDocument | null;
    document?: Record<string, unknown> | null;
    eol?: EOLMeta | null;
};

export type Doc = {
    id: number;
    type: string;
    ingestedFileId: number;
    meta: DocMeta;
    links: string[];
    presignedUrl?: string;
};

export type ComponentDoc = Doc & {
    serialNumber: string;
};
